import { useCallback, useMemo } from 'react'
import { Outlet } from 'react-router-dom'

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { DocumentsDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { asDocuments } from 'modules/documents/components/dataCollectionAdapters'
import { SPACES } from 'modules/documents/lib/constants'

import { useFilters } from './useFilters'
import { useColumns } from './useColumns'
import { useBulkActions } from './useBulkActions'
import { useItemActions } from './useItemActions'
import type { DocumentNode } from './types'

const SPACE = SPACES.EMPLOYEE_MY_DOCUMENTS
const ITEMS_PER_PAGE = 100

const DataCollection = () => {
  const { navigation } = useNavigation()
  const { filters, loading: filtersLoading } = useFilters()
  const { sortings, defaultSortings, columns } = useColumns()
  const { bulkActions, handleBulkAction, Modals } = useBulkActions()
  const { itemActions, ItemActionModals } = useItemActions()

  const source = useGraphqlDataCollectionSource({
    document: DocumentsDataCollectionDocument,
    paginationType: 'pages',
    perPage: ITEMS_PER_PAGE,
    includePaginationVars: false,

    mapData: (data) => {
      const edges = data?.documents?.documentsConnection?.edges ?? []
      return edges.map((edge) => edge.node)
    },

    mapVariables: ({ filters: filterState, sortings: sortingsState, search, pagination }) => {
      // Build DocumentsDocumentFilter from filter state
      const filter: Record<string, unknown> = {}

      const toStringArray = (val: unknown): string[] =>
        Array.isArray(val) ? val.filter((v): v is string => typeof v === 'string') : []

      if (filterState?.createdAt) {
        const raw = filterState.createdAt
        if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
          const from = 'from' in raw && raw.from instanceof Date ? raw.from : undefined
          const to = 'to' in raw && raw.to instanceof Date ? raw.to : undefined
          if (from && to) {
            filter.createdAt = {
              between: [from.toISOString(), to.toISOString()],
            }
          }
        }
      }

      if (filterState?.employee) {
        const employeeIds = toStringArray(filterState.employee)
        if (employeeIds.length > 0) {
          filter.employee = { in: employeeIds }
        }
      }

      if (filterState?.employeeStatus) {
        // Each option value is a comma-separated string of employee IDs
        const statusValues = toStringArray(filterState.employeeStatus)
        if (statusValues.length > 0) {
          const allIds = statusValues.flatMap((v) => v.split(','))
          filter.employee = { in: allIds }
        }
      }

      if (filterState?.folder) {
        const folderIds = toStringArray(filterState.folder)
        if (folderIds.length > 0) {
          // folder filter uses eq for single, in for multiple
          filter.folder = folderIds.length === 1 ? { eq: folderIds[0] } : { in: folderIds }
        }
      }

      if (filterState?.legalEntity) {
        const leIds = toStringArray(filterState.legalEntity)
        if (leIds.length > 0) {
          filter.legalEntity = { in: leIds }
        }
      }

      if (filterState?.location) {
        const locationIds = toStringArray(filterState.location)
        if (locationIds.length > 0) {
          filter.location = { in: locationIds }
        }
      }

      if (filterState?.signatureStatus) {
        const statuses = toStringArray(filterState.signatureStatus)
        if (statuses.length > 0) {
          filter.signatureStatus = { in: statuses }
        }
      }

      if (filterState?.team) {
        const teamIds = toStringArray(filterState.team)
        if (teamIds.length > 0) {
          filter.team = { in: teamIds }
        }
      }

      if (filterState?.visible) {
        const visibleValues = toStringArray(filterState.visible)
        if (visibleValues.length === 1) {
          filter.published = { eq: visibleValues[0] === 'true' }
        }
      }

      return {
        space: SPACE,
        search: search || undefined,
        filter: Object.keys(filter).length > 0 ? filter : undefined,
        sortOrder: sortingsState?.[0],
        // Manually map pagination since our query uses offsetPage/first, not page/perPage
        first: pagination?.perPage ?? ITEMS_PER_PAGE,
        offsetPage: pagination?.currentPage ?? 1,
      }
    },

    mapPagination: (data) => {
      const connection = data?.documents?.documentsConnection
      return {
        total: connection?.totalCount ?? 0,
      }
    },

    search: {
      enabled: true,
    },
    filters,
    sortings,
    defaultSortings,
    selectable: (item: DocumentNode) => item.id,
    bulkActions,
    itemActions,
    itemUrl: useCallback(
      (item: DocumentNode) =>
        navigation.files.employeeFiles.list.documentId.fullPath({
          document_id: item.id,
        }),
      [navigation]
    ),
  })

  const { data: collectionData } = useDataCollectionData(source)
  const records = collectionData?.records
  const documentsFromCollection = useMemo(() => asDocuments(records), [records])

  const outletContext = useMemo(
    () => ({
      documentsEntity: documentsFromCollection,
      parentRoute: navigation.files.employeeFiles.fullPath(),
    }),
    [documentsFromCollection, navigation]
  )

  if (filtersLoading) {
    return (
      <Box alignItems="center" padding="s64">
        <Spinner />
      </Box>
    )
  }

  return (
    <>
      <Modals />
      <ItemActionModals />
      <Box flexDirection="column" height="full">
        <Box flexGrow paddingX="s12" paddingTop="s12">
          <OneDataCollection
            fullHeight
            emptyStates={{
              'no-data': {
                title: i18n.t('documents.documents_datatable.empty_state.blank_state.title'),
                description: '',
              },
              'no-results': {
                title: i18n.t('documents.documents_datatable.empty_state.with_filters.title'),
                description: i18n.t(
                  'documents.documents_datatable.empty_state.with_filters.description'
                ),
              },
            }}
            source={source}
            onBulkAction={handleBulkAction}
            visualizations={[
              {
                type: 'table',
                options: {
                  allowColumnHiding: true,
                  columns,
                },
              },
            ]}
          />
        </Box>
      </Box>
      <Outlet context={outletContext} />
    </>
  )
}

export default DataCollection
